import { Phone, MapPin, Instagram, Mail, Youtube, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/masonry-pro-logo-3.webp";
import { Button } from "@/components/ui/button";
import { TikTokIcon, GoogleIcon, YelpIcon, AngiIcon } from "@/components/icons/SocialIcons";

export function Footer() {
  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Logo and contact */}
          <div>
            <img 
              src={logo} 
              alt="Masonry Pro Construction" 
              className="h-16 w-auto mb-6"
              width={160}
              height={64}
            />
            <p className="text-muted-foreground mb-6">
              35+ years of excellence in North Jersey masonry. Family-owned and operated.
            </p>
            
            <div className="space-y-3">
              <a href="tel:+19737252395" className="flex items-center gap-3 text-foreground hover:text-secondary transition-colors">
                <Phone className="h-5 w-5 text-secondary" />
                (973) 725-2395
              </a>
              <div className="flex items-start gap-3 text-foreground">
                <MapPin className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>
                  36 Highview Ave<br />
                  Totowa, NJ 07512
                </span>
              </div>
              <a href="mailto:masonryproconstruction@aol.com" className="flex items-center gap-3 text-foreground hover:text-secondary transition-colors">
                <Mail className="h-5 w-5 text-secondary" />
                masonryproconstruction@aol.com
              </a>
              
              {/* Social Icons Row */}
              <div className="flex items-center gap-4 pt-2">
                <a href="https://instagram.com/masonrypro" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="Instagram">
                  <Instagram className="h-6 w-6" />
                </a>
                <a href="https://www.youtube.com/@MasonryProConstruction" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="YouTube">
                  <Youtube className="h-6 w-6" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61557283318193" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="Facebook">
                  <Facebook className="h-6 w-6" />
                </a>
                <a href="https://www.tiktok.com/@masonryproconstruction" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="TikTok">
                  <TikTokIcon className="h-6 w-6" />
                </a>
                <a href="https://share.google/fJp672j4OedRwFH5N" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="Google Business Profile">
                  <GoogleIcon className="h-6 w-6" />
                </a>
                <a href="https://www.yelp.com/biz/masonry-pro-construction-totowa" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="Yelp">
                  <YelpIcon className="h-6 w-6" />
                </a>
                <a href="https://www.angi.com/companylist/us/nj/totowa/masonry-pro-construction-reviews-8052043.htm" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-secondary/80 transition-colors" aria-label="Angi">
                  <AngiIcon className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="text-muted-foreground hover:text-secondary transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/service-areas" className="text-muted-foreground hover:text-secondary transition-colors">
                  Service Areas
                </Link>
              </li>
              <li>
                <Button asChild variant="goldOutline" size="sm">
                  <Link to="/packages">Project Packages</Link>
                </Button>
              </li>
            </ul>
          </div>

          {/* Trust badges */}
          <div>
            <h3 className="text-lg font-bold mb-6">Why Choose Us</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li>✓ 5-Star Google Rating</li>
              <li>✓ A+ BBB Rated</li>
              <li>✓ Licensed & Insured</li>
              <li>✓ Free Estimates</li>
              <li>✓ Warranty Backed</li>
              <li>✓ NJ HIC License{" "}
                <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Masonry Pro Construction. All rights reserved. | Website created by{" "}
            <a href="https://www.gomonsteramarketing.com/" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">Monstera Marketing</a>.
          </p>
          <div className="flex items-center gap-4 text-muted-foreground text-sm">
            <Link to="/pro-tips" className="hover:text-secondary transition-colors">Pro Tips</Link>
            <span>|</span>
            <Link to="/privacy-policy" className="hover:text-secondary transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-and-conditions" className="hover:text-secondary transition-colors">Terms & Conditions</Link>
            <span>|</span>
            <Link to="/data-request" className="hover:text-secondary transition-colors">Privacy Rights Request</Link>
          </div>
          <p className="text-muted-foreground text-sm">
            Serving Bergen, Essex, Passaic & Morris Counties, NJ
          </p>
        </div>
      </div>
    </footer>
  );
}
